import useResizeObserver from '@react-hook/resize-observer';
import classNames from 'classnames';
import lottie from 'lottie-web/build/player/lottie_light';
import {InferGetStaticPropsType} from 'next';
import Image from 'next/image';
import React, {useCallback, useEffect, useRef, useState} from 'react';
import ReactSlider from 'react-slider';

import Col from '../../features/common/Col';
import {useEnvironment} from '../../features/common/EnvironmentProvider';
import Row from '../../features/common/Row';
import {getCmsEntry} from '../../features/strapi';
import {ApiFooterFooter} from '../../features/strapi/generated/contentTypes';
import Button from '../../features/tool/components/buttons/Button';
import SquareButton from '../../features/tool/components/buttons/SquareButton';
import CropAnimationListItem from '../../features/tool/components/crops/CropAnimationListItem';
import Footer from '../../features/tool/components/Footer';
import {getToolLayout} from '../../features/tool/components/Layout';
import Newsletter from '../../features/tool/components/Newsletter';
import {CROP_FAMILIES, CropFamilyId} from '../../features/tool/resources/cropFamilies';
import {buildStaticPropsResponse} from '../../features/tool/util/dataFetch';
import {GrowingCropsIcon} from '../../resources/Icons';
import URLS from '../../urls';
import {NextPageWithLayout} from '../_app';

const CropsPage: NextPageWithLayout<InferGetStaticPropsType<typeof getStaticProps>> = ({footer}) => {
  const environment = useEnvironment();

  const [currentStep, setCurrentStep] = useState(0);
  const targetStep = useRef(0);
  const [sliderDisabled, setSliderDisabled] = useState(false);
  const scrollableRef = useRef<HTMLDivElement>(null);
  const [scrollAtLeft, setScrollAtLeft] = useState(true);
  const [scrollAtRight, setScrollAtRight] = useState(false);

  const handleClickLeft = useCallback(() => {
    scrollableRef.current?.scrollBy({left: -200, behavior: 'smooth'});
  }, []);

  const handleClickRight = useCallback(() => {
    scrollableRef.current?.scrollBy({left: 200, behavior: 'smooth'});
  }, []);

  const handleScroll = useCallback(() => {
    if (!scrollableRef.current) {
      return;
    }
    const scrollableDiv = scrollableRef.current;
    setScrollAtLeft(scrollableDiv.scrollLeft === 0);
    setScrollAtRight(scrollableDiv.scrollLeft === scrollableDiv.scrollWidth - scrollableDiv.offsetWidth);
  }, []);

  useResizeObserver(scrollableRef, handleScroll);

  const handleSliderChange = useCallback(
    (index: number) => {
      targetStep.current = index;
      if (index < currentStep) {
        setCurrentStep(index);
        lottie.stop(); // This resets animation when going back from last step to second last
        return;
      }
      if (index > currentStep) {
        setSliderDisabled(true);
        lottie.play();
      }
    },
    [currentStep]
  );

  const handleAnimationComplete = useCallback(
    (id: CropFamilyId, step: number) => {
      if (id === CropFamilyId.miscanthus) {
        // lottie.play() will play all animations, so we do this only for one animation (miscanthus in this case)
        if (step < targetStep.current) {
          setCurrentStep(prev => prev + 1);
        }
      }
    },
    [targetStep]
  );

  // Triggered whenever currentStep changes
  useEffect(() => {
    setSliderDisabled(currentStep !== targetStep.current);

    if (currentStep < targetStep.current) {
      lottie.play();
    }
  }, [currentStep]);

  return (
    <>
      <Col className={'relative w-full pt-xl pb-md items-center gap-4 bg-crops-sky overflow-hidden'}>
        <div className={'absolute inset-0 mx-auto max-w-wider'}>
          <Image
            src={'/img/crops/cloud1.svg'}
            width={278}
            height={100}
            alt="cloud"
            objectFit="cover"
            className={'absolute bottom-0 left-[-28rem] z-10'}
            aria-hidden
          />
          <Image
            src={'/img/crops/cloud2.svg'}
            width={140}
            height={79}
            alt="cloud"
            objectFit="cover"
            className={'absolute bottom-[6rem] left-[-7rem] z-10'}
            aria-hidden
          />
          <Image
            src={'/img/crops/cloud3.svg'}
            width={194}
            height={83}
            alt="cloud"
            objectFit="cover"
            className={'absolute bottom-[1rem] right-[-24rem] z-10'}
            aria-hidden
          />
        </div>
        <div className="z-10 w-full max-w-wider px-md text-primary-600 font-semibold text-[1.6rem]">CROPS</div>
        <div className="z-10 w-full max-w-wider px-md text-huge font-semibold leading-tight">Explore Crop Types</div>
      </Col>
      <Row
        ref={scrollableRef}
        onScroll={handleScroll}
        className={
          'relative w-full bg-crops-sky bg-[url(/img/crops/earthbg.svg)] bg-repeat-x bg-[position:50%_356px] overflow-scroll'
        }
      >
        <SquareButton
          className={classNames(
            'sticky z-10 left-[10px] ml-auto !border-primary-800 flex-none self-center',
            scrollAtLeft && 'invisible'
          )}
          direction="left"
          variant={'secondary'}
          onClick={handleClickLeft}
        />
        <div className={'grow min-w-fit max-w-wider px-sm flex-none'}>
          <ul className={'w-full min-w-fit h-[460px] relative p-0 flex justify-between gap-xs overflow-hidden'}>
            {CROP_FAMILIES.map(({id, name}) => (
              <CropAnimationListItem
                key={id}
                id={id}
                name={name}
                step={currentStep}
                onComplete={handleAnimationComplete}
              />
            ))}
          </ul>
        </div>
        <SquareButton
          className={classNames(
            'sticky z-10 right-[10px] mr-auto !border-primary-800 flex-none self-center',
            scrollAtRight && 'invisible'
          )}
          direction="right"
          variant={'secondary'}
          onClick={handleClickRight}
        />
      </Row>
      <Col className={'w-full pt-sm pb-lg items-center bg-crops-earth'}>
        <Col className="w-full max-w-wider px-lg pb-xl gap-sm text-white ">
          <p className={'-ml-xs text-lg font-semibold'}>Explore the crop growth timeline</p>
          <ReactSlider
            className={classNames('w-full crop-slider', sliderDisabled && 'cursor-not-allowed opacity-30')}
            thumbClassName="crop-slider-thumb"
            trackClassName="crop-slider-track"
            markClassName="crop-slider-mark"
            renderMark={props => (
              <div {...props} className={'crop-slider-mark'}>
                <div className={'w-fit pt-xs whitespace-nowrap -translate-x-1/2'}>
                  {sliderLabel(parseInt(String(props.key)))}
                </div>
              </div>
            )}
            onChange={handleSliderChange}
            min={0}
            max={4}
            marks={true}
            disabled={sliderDisabled}
          />
        </Col>
      </Col>
      <div className="bg-primary-700 text-white">
        <Row className="w-full max-w-wider mx-auto px-sm py-lg items-center gap-xs flex-wrap">
          <GrowingCropsIcon className={'flex-none'} />
          <div className={'grow'}>
            <p className={'text-lg font-semibold'}>Determine which crops are best for your land</p>
            <p>As well as learning their estimated yields, costs and timescales.</p>
          </div>
          <Button
            variant={'secondary'}
            accessory={'forward'}
            className={'!border-white hover:!border-primary-300 !bg-primary-700 !text-white'}
            href={URLS.start}
          >
            Get started
          </Button>
        </Row>
      </div>
      {environment && <Newsletter data={environment.newsletter} />}
      <Footer data={footer} />
    </>
  );
};

function sliderLabel(index: number) {
  switch (index) {
    case 0:
      return '0';
    case 1:
      return '3 months';
    case 2:
      return '1 year';
    case 3:
      return '3 years';
    case 4:
      return '6 years';
    default:
      return '';
  }
}

export const getStaticProps = async () => {
  return buildStaticPropsResponse({
    ...(await getCmsEntry<ApiFooterFooter, 'footer'>({path: 'footer'})),
  });
};

export default CropsPage;

CropsPage.getLayout = getToolLayout;

CropsPage.title = 'Crops';
